/**
 * This component is responsible for:
 * 1. Rendering calendar component with days
 * 2. Fetching available appointments based on time frame of 60 from current date
 * 3. Filtering appointments based on selected appointment type
 * 4. Setting first available day based on filtered appointments
 * 5. Setting timezone abbreviation based on available appointments
 * 6. Navigating to request appointment if no availability
 * 7. Handling calendar date changes
 * 8. Handling today button click
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useReactiveVar, useSuspenseQuery } from "@apollo/client";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import { Typography } from "@mui/material";
import Button from "@mui/material/Button";
import * as Sentry from "@sentry/browser";
import {
  add,
  addDays,
  differenceInDays,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  formatISO,
  intlFormat,
  isSameDay,
  parse,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import PropTypes from "prop-types";
import { Analytics } from "shared-components";
import { Divider } from "shared-components";

import {
  alertType,
  appointmentsAvailable,
  message,
  store,
  timezoneAbbr,
} from "../../cache";
import i18next from "../../i18n";
import { APPOINTMENT_AVAILABILITY } from "../../queries/appointmentAvailability";
import { getAvailability } from "../../utils/getAvailability";
import { newestToOldest } from "../../utils/newestToOldest";
import { toISOStringDate } from "../../utils/toISOStringDate";
import { Day } from "../Day";
import { FirstAvailable } from "../FirstAvailable";
import { NextMonth } from "./NextMonth";
import { PreviousMonth } from "./PreviousMonth";

import "./style.css";

export const Calendar = ({
  onDaySelected,
  navigate,
  selectedDay,
  selectedAppointmentType,
}) => {
  // Build calendar state based on today
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const weeks = eachWeekOfInterval({ start: monthStart, end: monthEnd });

  // Setup translation and localization
  const { t } = useTranslation();
  const langCode = i18next.language;
  const invalidDateMessage = t(
    "Oops! It looks like there is an issue with your request, please try using a different browser or try again later. If this issue persists please contact support@intelibly.com."
  );

  // Get the localized month
  const currentMonthYear = intlFormat(
    currentDate,
    {
      year: "numeric",
      month: "long",
    },
    { locale: langCode }
  );

  // Get the localized weekdays
  const localizedWeekdays = () => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
    const weekdays = Array.from({ length: 7 }).map((_, i) => {
      return intlFormat(
        addDays(weekStart, i),
        { weekday: "short" },
        { locale: langCode }
      );
    });
    return weekdays;
  };
  const weekdays = localizedWeekdays();

  // Setup appointment query variables
  const [startDate, setStartDate] = useState(currentDate);
  const endDate = add(startDate, { days: 60 });

  // Get locationId for the query and partnerId for analytics
  const { locationId, partnerId } = useReactiveVar(store);

  // State for available appointments since we are merging appointments when the user navigates beyond 60 days
  const [availableAppointments, setAvailableAppointments] = useState([]);

  // State  when the query has completed this is not available when using SuspenseQuery we use it  we need to navigate to request appointment
  const [queryCompleted, setQueryCompleted] = useState(false);

  // State for when the first available day is found
  const [firstAvailableDay, setFirstAvailableDay] = useState(null);

  // State of when upstream vendor service is unavailable
  const [vendorServiceUnavailable, setVendorServiceUnavailable] =
    useState(false);

  /**
   * Filter appointments by selected appointment type
   */
  const filteredAppointments = useMemo(() => {
    if (!selectedAppointmentType) return availableAppointments;

    return availableAppointments.filter(
      (appointment) =>
        appointment?.appointmentType?.id === selectedAppointmentType.id
    );
  }, [availableAppointments, selectedAppointmentType]);

  // Fetch available appointments based on locationId, startDate, and endDate
  const { data } = useSuspenseQuery(APPOINTMENT_AVAILABILITY, {
    variables: {
      locationId,
      startDateUtc: toISOStringDate(startDate),
      endDateUtc: toISOStringDate(endDate),
    },
  });

  /**
   * Update start date when we reach end of 60 day period
   * We do this to refetch the available appointments for the next period
   */
  useEffect(() => {
    if (differenceInDays(endDate, currentDate) < 0) {
      setStartDate(endDate);
    }
  }, [currentDate, endDate]);

  /**
   * Parse response and merge appointment data into availableAppointments state
   */
  useEffect(() => {
    switch (data.pmsAvailableAppointments.__typename) {
      case "PmsAvailableAppointments":
        if (data.pmsAvailableAppointments.appointments.length > 0) {
          const combinedAvailableAppointments = availableAppointments.concat(
            data.pmsAvailableAppointments.appointments
          );
          setAvailableAppointments(combinedAvailableAppointments);
          appointmentsAvailable(true);
        } else {
          appointmentsAvailable(false);
        }
        // Even if the avilable appointment list is empty we still need to indicate that query has completed
        setQueryCompleted(true);
        break;
      case "VendorServiceUnavailable":
        //Vendor service might be down so we update the state and navigate to request appointment
        setVendorServiceUnavailable(true);
        appointmentsAvailable(false);
        Sentry.captureMessage(
          "Vendor Service Unavailable: ${data.pmsAvailableAppointments.message}"
        );
        navigate("/request-appointment");
        break;
      case "InvalidStartDate":
        //We tried fetching for appointments in the past
        alertType("warning");
        message(invalidDateMessage);
        Sentry.captureMessage(
          "Invalid Start Date: ${data.pmsAvailableAppointments.message}"
        );
        break;
      default:
        // Uncaught error
        Sentry.captureMessage(`Uncaught error in PMS`);
        break;
    }
  }, [data]);

  /**
   * Set first available date from filtered appointments
   */
  useEffect(() => {
    if (filteredAppointments.length > 0) {
      const sortedAppointments = filteredAppointments.toSorted(newestToOldest);
      const firstAvailableDay = parse(
        sortedAppointments[0].date,
        "yyyy-MM-dd",
        new Date()
      );

      const availability = getAvailability(
        firstAvailableDay,
        filteredAppointments
      );

      setFirstAvailableDay({
        date: new Date(firstAvailableDay),
        times: availability.times,
      });
    } else {
      // Reset if no appointments found for selected appointment type
      onDaySelected(null);
      setFirstAvailableDay(null);
    }
  }, [filteredAppointments]);

  /**
   * Set timezone abbreviation state based on available appointments.
   */
  useEffect(() => {
    if (availableAppointments.length > 0) {
      timezoneAbbr(availableAppointments[0].timezoneAbbr);
    }
  }, [availableAppointments]);

  /**
   * Forward to request appointment if no availability or if the vendorService is down
   */
  useEffect(() => {
    if (vendorServiceUnavailable) {
      navigate("/request-appointment");
    }
    if (queryCompleted && availableAppointments.length === 0) {
      navigate("/request-appointment");
    }
  }, [availableAppointments, queryCompleted, vendorServiceUnavailable]);

  /**
   * ! Commenting this out will allow the calendar to show the current month when fetching the next 60days
   * Display current month of the selectedDay when using firstAvailable to change the selectedDay
   */
  useEffect(() => {
    if (selectedDay && !isSameDay(selectedDay, currentDate)) {
      setCurrentDate(selectedDay);
    }
  }, [selectedDay]);

  /**
   * Update selected date when calendar date changes
   * @param {Date} date - The selected date
   * @return {void}
   */
  const setCalendarDate = (date) => {
    setCurrentDate(date);
  };

  /**
   * Goes to today's date in the calendar and performs necessary actions.
   * @return {void}
   */
  const goToToday = () => {
    Analytics.todaySelected({
      locationId,
      partnerId,
    });
    const todayAvailability = getAvailability(today, filteredAppointments);
    if (todayAvailability.times.length > 0) {
      setCalendarDate(today, false);
      onDaySelected(today, todayAvailability);
    } else {
      setCalendarDate(today);
      onDaySelected(null);
    }
  };

  return (
    <div className="calendar">
      <div className="calendar-header">
        <Typography
          className="month-year"
          sx={{
            color: "var(--intelibly-branding-neutral-900)",
            fontWeight: 500,
          }}
          variant="h6"
        >
          {currentMonthYear}
        </Typography>
        <div className="arrow-button-container">
          <PreviousMonth
            currentDate={currentDate}
            today={today}
            setCalendarDate={setCalendarDate}
            monthStart={new Date(monthStart)}
          />
          <NextMonth
            currentDate={currentDate}
            setCalendarDate={setCalendarDate}
          />
        </div>
      </div>
      <Divider variant="secondary" />
      <Button
        aria-label="today"
        color="primary"
        fullWidth
        onClick={() => goToToday()}
        startIcon={<TodayRoundedIcon />}
        variant="text"
      >
        {t("Today")}
      </Button>
      <div className="date-picker">
        <ul className="weekdays">
          {weekdays.map((day) => (
            <li key={day}>
              <Typography variant="body2" className="week-day" key={day}>
                {day}
              </Typography>
            </li>
          ))}
        </ul>
        <ol className="days-grid">
          {weeks.map((startOfWeek) =>
            eachDayOfInterval({
              start: startOfWeek,
              end: endOfWeek(startOfWeek),
            }).map((day) => (
              <li key={formatISO(day, { representation: "date" })}>
                <Day
                  day={new Date(day)}
                  availability={getAvailability(day, filteredAppointments)}
                  onSelectDay={onDaySelected}
                  selectedDay={selectedDay}
                />
              </li>
            ))
          )}
        </ol>
      </div>
      {!selectedDay && firstAvailableDay && (
        <FirstAvailable
          firstAvailableDay={firstAvailableDay}
          onDaySelected={onDaySelected}
          selectedDay={selectedDay}
        />
      )}
    </div>
  );
};

Calendar.propTypes = {
  onDaySelected: PropTypes.func,
  selectedDay: PropTypes.instanceOf(Date),
  navigate: PropTypes.func,
  selectedAppointmentType: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
};
